import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: i * 0.06 },
  }),
};

export default function PortfolioPage() {
  const { t } = useLanguage();
  const items = t.portfolio.items;

  return (
    <main className="bg-white" data-testid="portfolio-page">
      {/* Header */}
      <section className="relative overflow-hidden pt-40 pb-20 sm:pt-52 sm:pb-28 bg-[#fbd9e5]">
        <div aria-hidden className="pointer-events-none absolute -top-40 -right-40 h-[600px] w-[600px] rounded-full bg-white/50 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="text-xs sm:text-sm font-bold uppercase tracking-[0.3em] text-[#3e2723]/60"
          >
            {t.portfolio.eyebrow}
          </motion.p>
          <motion.h1
            variants={fadeUp}
            custom={1}
            initial="hidden"
            animate="visible"
            className="mt-6 font-display text-5xl sm:text-7xl lg:text-8xl font-black tracking-tighter leading-[0.95] text-[#3e2723] max-w-5xl"
          >
            {t.portfolio.title}
          </motion.h1>
          <motion.p
            variants={fadeUp}
            custom={2}
            initial="hidden"
            animate="visible"
            className="mt-8 max-w-2xl text-lg text-[#3e2723]/75 leading-relaxed"
          >
            {t.portfolio.subtitle}
          </motion.p>
        </div>
      </section>

      {/* Grid */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
          <div className="grid gap-5 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((item, i) => (
              <motion.article
                key={item.name}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                className="group relative overflow-hidden rounded-[1.75rem] sm:rounded-[2rem] aspect-[4/5] cursor-pointer"
                data-testid={`portfolio-item-${i}`}
              >
                <img
                  src={item.image}
                  alt={item.name}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#3e2723]/90 via-[#3e2723]/30 to-transparent transition-opacity duration-500 group-hover:from-[#3e2723]/95" />

                {/* Service tag (top) */}
                <div className="absolute top-5 left-5">
                  <span className="inline-flex items-center rounded-full bg-[#fbd9e5]/95 backdrop-blur-sm text-[#3e2723] px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] shadow-sm">
                    {item.service}
                  </span>
                </div>

                {/* Project name (bottom) */}
                <div className="absolute inset-x-5 bottom-5 flex items-end justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-[#fbd9e5] leading-tight">
                      {item.name}
                    </h3>
                    <p className="mt-1 text-[12px] text-[#fbd9e5]/80">{item.service}</p>
                  </div>
                  <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#fbd9e5] text-[#3e2723] group-hover:rotate-45 transition-transform duration-500">
                    <ArrowUpRight className="h-4 w-4" />
                  </span>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#faf8f8] py-24 sm:py-32">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="font-display text-4xl sm:text-6xl font-black tracking-tighter text-[#3e2723] leading-[1.05]">
            {t.portfolio.cta}
          </h2>
          <Link
            to="/contact"
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#3e2723] text-[#fbd9e5] px-8 py-4 text-[15px] font-semibold hover:-translate-y-1 hover:bg-[#2a1a17] transition-all duration-300 shadow-lg"
            data-testid="portfolio-cta"
          >
            {t.nav.cta}
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </main>
  );
}
